<template>
  <AmLoading
    v-if="loading"
    :isLoading="loading"
    class="mx-auto pb-10 absolute"
  />
  <div
    v-else
    :class="['app-success max-w-lg', `app-success--${mq.current}`]">
    <div class="app-success__header">
      <AmIconMaterial
        v-if="mq.current !== 'mobile'"
        class="app-success__header-icon"
        name="check_circle"
        color="#9B6AFA"
      />

      <AmTypography
        :text="status.title"
        textColor="primaryPurple120"
        weight="medium"
        :typographyStyle="mq.current === 'mobile' ? 'l4' : 'l3'"
      />
    </div>
    <div class="app-success__status">
      <AmTypography
        :text="$t(`appSuccess.labels.appSituation`)"
        textColor="text100"
        typographyStyle="l5"
      />
      <AmChip
        :text="$t(`appSuccess.status.${status.chipStatus}`)"
        :type="status.chipType"
        :data-test="`status-tag`"
        isStatusTag
      />
    </div>
    <div
      v-if="status.subTitle"
      class="app-success__subtitle text-justify max-w-md md:max-w-[350px]"
    >
      <AmTypography
        class="text-center"
        :text="status.subTitle"
        typographyStyle="l7"
        textColor="text100"
      />
    </div>
    <div
      v-if="status.content"
      class="app-success__content">
      <div
        v-if="status.contentType === 'text'"
        class="app-success__content-text"
      >
        <AmTypography
          :text="status.content.text"
          typographyStyle="l6"
          textColor="text100"
          class="text-center"
          :enfaseText="
            status.content.enfaseText
              ? status.content.enfaseText
              : null
          "
          :nextText="
            status.content.nextText ? status.content.nextText : null
          "
        />
      </div>
      <div
        v-if="status.contentType === 'list'"
        class="app-success__content-list"
      >
        <ol class="app-success__content-list-item">
          <li
            v-for="(item, index) in status.content"
            :key="index"
            :class="item.classes"
          >
            <AmTypography
              v-if="!item.htmlContent"
              :text="item.title"
              textColor="text100"
              :enfaseText="
                item.enfaseText ? item.enfaseText : null
              "
              :nextText="item.nextText ? item.nextText : null"
              typographyStyle="l6"
            />
            <div
              v-if="item.htmlContent"
              :class="item.classes"
              v-html="item.htmlContent"
            ></div>
          </li>
        </ol>
      </div>
    </div>

    <div
      v-if="status.contentType === 'html'"
      :class="['app-success__content-html', status.classes]"
      v-html="status.htmlContent"
    ></div>

    <div
      v-if="status.actions"
      class="app-success__actions mt-8">
      <AmButton
        :label="$t('appSuccess.buttonsLabel.viewApp')"
        data-test="redirect-app-success-view-app-button"
        size="xxlarge"
        variant="maxPrimary"
        @click="goToAppDetails"
      />
    </div>
  </div>
</template>

<script setup>
import './AppSuccess.scss'
import {
  AmTypography,
  AmChip,
  AmButton,
  AmLoading,
  AmIconMaterial
} from '@appmax_npm/ds-appmax-v3'
import { computed, inject, ref, onBeforeMount, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useReturnBarStore } from '@/stores/returnBar'
import axios from 'axios'
import _ from 'lodash'

const route = useRoute()
const router = useRouter()
const returnBarStore = useReturnBarStore()
const { t } = useI18n()
const mq = inject('mq')
const env = inject('env')

const status = ref(null)
const loading = ref(false)

const docURL = `${env.app.documentation}`
const myAppsURL = `${env.app.baseURL}/apps`

const goToAppDetails = () => {
  // O id do app vem de route.params.id (rota /app/:id/status).
  // Se nao houver id nos params, redireciona para a lista de apps.
  if (route.params.id) {
    router.push({ name: 'app', params: { id: route.params.id } })
  } else {
    router.push({ name: 'apps' })
  }
}

const defineFeedback = status => {
  return {
    inDraft: {
      title: computed(() => t('appSuccess.inDraft.title')),
      chipStatus: status,
      chipType: 'lightGray',
      subTitle: computed(() => t('appSuccess.inDraft.subTitle')),
      actions: true,
      contentType: null
    },
    development: {
      title: computed(() => t('appSuccess.success.title')),
      chipStatus: status,
      chipType: 'warning',
      subTitle: computed(() => t('appSuccess.success.subTitle')),
      actions: true,
      contentType: 'list',
      content: [
        {
          htmlContent: `
                    <span>
                        ${t('appSuccess.success.content.firstItem.title')}
                        <a class="text-primary font-medium" href="${docURL}">
                            ${t('appSuccess.success.content.firstItem.documentation')}
                        </a>
                        ${t('appSuccess.success.content.firstItem.finalText')}
                    </span>`
        },
        {
          htmlContent: `
                    <span>
                            ${t('appSuccess.success.content.secondItem.title')}
                        <a class="text-primary font-medium" href="${myAppsURL}">
                            ${t('appSuccess.success.content.secondItem.enfaseText')}
                        </a>
                            ${t('appSuccess.success.content.secondItem.middleText')}
                        <a class="text-primary font-medium" href="${myAppsURL}">
                            ${t('appSuccess.success.content.secondItem.appsInDevelopment')}
                        </a>
                            ${t('appSuccess.success.content.secondItem.click')}
                        <a class="text-primary font-medium" href="${docURL}">
                            ${t('appSuccess.success.content.secondItem.endText')}
                        </a>
                    </span>`
        },
        {
          htmlContent: `
                    <span>
                        ${t('appSuccess.success.content.thirdItem.title')}
                        <a class="text-primary font-medium" href="${myAppsURL}">
                            ${t('appSuccess.success.content.thirdItem.enfaseText')}
                        </a>
                    </span>`
        }
      ]
    },
    inAnalysis: {
      title: computed(() => t('appSuccess.inAnalysis.title')),
      chipStatus: status,
      chipType: 'systemBlue120',
      subTitle: computed(() => t('appSuccess.inAnalysis.subTitle')),
      actions: true,
      contentType: 'html',
      classes: 'mb-32',
      htmlContent: `
                <div class="max-w-[420px] m-auto">
                    <span>
                        ${t('appSuccess.inAnalysis.content.text')}
                        <a class="text-primary font-medium" href="${myAppsURL}">
                            ${t('appSuccess.inAnalysis.content.enfaseText')}
                        </a>
                        ${t('appSuccess.inAnalysis.content.otherText')}
                    </span>
                </div>
            `
    },
    inTest: {
      title: computed(() => t('appSuccess.inTest.title')),
      chipStatus: status,
      chipType: 'warning',
      subTitle: computed(() => t('appSuccess.inTest.subTitle')),
      actions: true,
      contentType: 'html',
      classes: 'mb-32',
      htmlContent: `
                <div class="max-w-[420px] m-auto">
                    <span>
                        ${t('appSuccess.inTest.content.text')}
                        <a class="text-primary font-medium" href="${myAppsURL}">
                            ${t('appSuccess.inTest.content.enfaseText')}
                        </a>
                        ${t('appSuccess.inTest.content.otherText')}
                    </span>
                </div>
            `
    }
  }[status]
}

onBeforeMount(async () => {
  loading.value = true

  await axios
    .get(`${env.api.appbox.baseURL}/app/app-status/${route.params.id}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('user-token')}`
      }
    })
    .then(response => {
      status.value = defineFeedback(_.camelCase(response.data.data))
      loading.value = false
    })
    .catch(error => {
      console.log(error)
    })
})

onUnmounted(() => {
  returnBarStore.action.path = ''
})
</script>
